/**
 * Zonen: the chat of a district (amendment 16).
 *
 * A zone is a res-7 cell — the overview's hexagon, about 1.2 km to an edge, a district rather
 * than a street. Coarse on purpose, twice over: big enough that a few people are in one at the
 * same time on an evening, and coarse enough that "is in Stuttgart-Mitte" says less about
 * somebody than any report they file.
 *
 * Pure like the rest of the directory: what a zone is, what a message may be, and how long
 * somebody counts as being in one. Which zone a device is in is worked out on the device;
 * nothing here is ever handed a position by the server.
 */

import { type Cell, cellFor, isCell, OVERVIEW_RESOLUTION, resolutionOf } from './h3.ts';

export const ZONE_RESOLUTION = OVERVIEW_RESOLUTION;

/** The longest message, in characters: a line on a lock screen and a thought, not a letter. */
export const MESSAGE_MAX = 280;

/**
 * How long somebody counts as in a zone after their device last said so. After two hours they
 * are gone from it at once — every read ignores the row — and the row itself goes with the next
 * quarter-hourly sweep.
 */
export const PRESENCE_TTL_MS = 2 * 60 * 60 * 1000;

/** At most one push per person and zone this often: a lively evening must not become a buzz. */
export const PUSH_GAP_SECONDS = 10 * 60;

/** Reports from this many different people hide a message until an admin looks (brief §18). */
export const HIDE_AFTER_REPORTS = 2;

export function isZone(value: unknown): value is Cell {
  return isCell(value) && resolutionOf(value) === ZONE_RESOLUTION;
}

/** The zone a position is in. Called on the device, which is the only place a position is. */
export function zoneOf(lat: number, lon: number): Cell {
  return cellFor(lat, lon, ZONE_RESOLUTION);
}

export type MessageCheck = { ok: true; value: string } | { ok: false; message: string };

/**
 * What a message may be: text, not empty once trimmed, and at most `MESSAGE_MAX` characters.
 *
 * Counted in characters rather than bytes or UTF-16 units, so an emoji is one, as anybody
 * typing would count it. Control characters other than the line break are dropped: they draw
 * nothing and can make a message look like something it is not.
 */
export function checkMessage(body: unknown): MessageCheck {
  if (typeof body !== 'string') return { ok: false, message: 'Die Nachricht fehlt.' };
  const chars = [...body.replace(/\r\n?/g, '\n')].filter((char) => {
    const code = char.codePointAt(0) ?? 0;
    return code === LINE_BREAK || (code >= SPACE && code !== DELETE);
  });
  const text = chars.join('').trim();
  if (text === '') return { ok: false, message: 'Die Nachricht ist leer.' };
  if ([...text].length > MESSAGE_MAX) {
    return { ok: false, message: `Höchstens ${MESSAGE_MAX} Zeichen.` };
  }
  return { ok: true, value: text };
}

const LINE_BREAK = 0x0a;
const SPACE = 0x20;
const DELETE = 0x7f;

/** One line for a lock screen: line breaks become spaces, and a long message is cut. */
export function previewOf(text: string, max = 80): string {
  const chars = [...text.replace(/\s+/g, ' ').trim()];
  return chars.length > max ? `${chars.slice(0, max - 1).join('')}…` : chars.join('');
}
